import { Box, Flex, StyleProps } from '@chakra-ui/react';
import { Children, cloneElement, FC, isValidElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@bonusly/ds-tooltip';

import { AvatarSize } from '../types';
import { AvatarProps, AvatarSizeMap } from './avatar';

export type AvatarGroupProps = StyleProps & {
	children?: ReactNode;
	max?: number;
	size?: AvatarProps['size'];
};

const OffsetSizeMap: Record<AvatarSize, string> = {
	sm: '-0.25rem',
	md: '-0.5rem',
	lg: '-0.75rem',
	xl: '-1rem',
	'2xl': '-1.25rem',
};

const FontSizeMap: Record<AvatarSize, string> = {
	sm: 'xs',
	md: 'sm',
	lg: 'md',
	xl: 'lg',
	'2xl': 'xl',
};

export const AvatarGroup: FC<AvatarGroupProps> = ({ children, max = 5, size = 'md', ...rest }) => {
	const { t } = useTranslation('common');
	const avatars = Children.toArray(children).slice(0, max);
	const remainingNames = Children.toArray(children)
		.slice(max)
		.map<string>((avatar) => {
			if (!isValidElement(avatar)) {
				return null;
			}

			return avatar.props.name;
		})
		.sort((a, b) => a.localeCompare(b));
	const displayNames = remainingNames.slice(0, 10);
	const extra = remainingNames.length - displayNames.length;
	const displayTooltip = displayNames.filter(Boolean).length > 0;

	return (
		<Flex {...rest}>
			{avatars.map((avatar) => {
				if (!isValidElement(avatar)) {
					return null;
				}

				return cloneElement<any>(avatar as any, { _notLast: { marginInlineEnd: OffsetSizeMap[size] }, size });
			})}

			{remainingNames.length > 0 && (
				<Tooltip
					isDisabled={!displayTooltip}
					openDelay={500}
					label={t('intl_overflow_list', {
						names: displayNames,
						count: extra,
					})}
				>
					<Box
						as="span"
						alignItems="center"
						bgColor="bg.subtle"
						borderRadius="full"
						boxSize={AvatarSizeMap[size]}
						color="text.default"
						fontSize={FontSizeMap[size]}
						display="inline-flex"
						flexShrink={0}
						justifyContent="center"
						transitionDuration="normal"
						transitionProperty="common"
						borderColor="bg.surface"
						borderWidth="1px"
						borderStyle="solid"
						{...(displayTooltip && {
							_hover: {
								bgColor: 'secondary.bg',
								color: 'secondary.fgOnSurface',
								cursor: 'default',
							},
						})}
					>
						+{remainingNames.length}
					</Box>
				</Tooltip>
			)}
		</Flex>
	);
};
